import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import DocumentViewerModal from "../../../components/ui/DocumentViewerModal";
import useIsMobile from "../../../hooks/useIsMobile";
import { safeFileUrl } from "../../../utils/safeUrl";

// Chrome en Android no dibuja PDF dentro de un iframe, tampoco en tablets anchas.
const IS_ANDROID = typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent || "");

// Los links "PDF" de evidencia del retiro abrían una pestaña nueva y el usuario volvía
// enseguida (quick back en Clarity). En desktop se muestra en un modal in-app; en móvil
// se mantiene el link a pestaña nueva porque Chrome Android no renderiza PDFs en iframes.
//
// Uso: const { pdfLink, pdfModal } = usePdfViewer(); pdfLink({ url, correlative, ... })
// donde quiera el link, y `{pdfModal}` UNA vez por componente (un solo modal compartido,
// no uno por fila).
export function usePdfViewer() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [doc, setDoc] = useState(null);

  const pdfLink = ({ url, correlative, className, title, children }) => {
    if (isMobile || IS_ANDROID) {
      // La URL viene de la API: un esquema ejecutable no llega a un href.
      const href = safeFileUrl(url);
      if (!href) return null;
      return (
        <a className={className} href={href} target="_blank" rel="noreferrer" title={title}>
          {children}
        </a>
      );
    }
    return (
      <button
        type="button"
        className={className}
        title={title}
        onClick={(e) => {
          // El link vive dentro de filas/popups clicables: no debe disparar al padre.
          e.stopPropagation();
          setDoc({
            url,
            title:
              correlative != null && correlative !== ""
                ? t("Evidencia del retiro #{{n}}", { n: correlative })
                : t("Documento"),
          });
        }}
      >
        {children}
      </button>
    );
  };

  const pdfModal = (
    <DocumentViewerModal
      open={!!doc}
      url={doc?.url}
      title={doc?.title}
      onClose={() => setDoc(null)}
    />
  );

  return { pdfLink, pdfModal };
}
